/**
 * Keyboard navigation for a queue: j/k move, Enter opens, Esc closes, `/` focuses the filter.
 *
 * An ops desk works one queue for a whole shift. Reaching for the mouse per row is the difference
 * between clearing a KYC backlog and half-clearing it, so every table in this console is drivable
 * from the home row.
 *
 * Two deliberate behaviours:
 *   - While the operator is typing in a field, letter keys are theirs, not ours. Only Escape is
 *     intercepted, and it blurs the field rather than closing the detail behind it.
 *   - Focus resets to the top when the row set changes — after a filter, row 4 of the old list is
 *     a different case, and acting on it by muscle memory is exactly the mistake to prevent.
 */
import { useState, useEffect } from 'react';

export interface QueueItem {
  /* Fixtures key rows by an opaque string; the API keys them by an integer primary key. The queue
     only ever compares identities, so it does not care which — and forcing one shape would mean
     converting an id on the way in and back on the way out, which is where ids get lost. */
  id: string | number;
}

export interface QueueOptions {
  /** Changing this resets focus — pass the active filter/tab so a re-filter starts at the top. */
  key?: string;
  /** Id of the filter field `/` focuses. Matches FilterBar's default. */
  searchId?: string;
}

export interface Queue<T> {
  focus: number;
  setFocus: (index: number) => void;
  open: T | null;
  setOpen: (row: T | null) => void;
}

function isTyping(target: EventTarget | null): boolean {
  const tag = (target as HTMLElement | null)?.tagName?.toLowerCase();
  return tag === 'input' || tag === 'textarea' || tag === 'select';
}

export function useQueue<T extends QueueItem>(rows: readonly T[], opts: QueueOptions = {}): Queue<T> {
  const { key, searchId = 'admin-search' } = opts;
  const [focus, setFocus] = useState(0);
  const [open, setOpen] = useState<T | null>(null);

  useEffect(() => {
    setFocus(0);
  }, [rows.length, key]);

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      const typing = isTyping(e.target);

      if (e.key === '/' && !typing) {
        e.preventDefault();
        document.getElementById(searchId)?.focus();
        return;
      }
      if (e.key === 'Escape') {
        if (typing) {
          (e.target as HTMLElement).blur();
          return;
        }
        setOpen(null);
        return;
      }
      if (typing) return;

      if (e.key === 'j' || e.key === 'ArrowDown') {
        e.preventDefault();
        setFocus((f) => Math.min(rows.length - 1, f + 1));
      } else if (e.key === 'k' || e.key === 'ArrowUp') {
        e.preventDefault();
        setFocus((f) => Math.max(0, f - 1));
      } else if (e.key === 'Enter') {
        e.preventDefault();
        setOpen(rows[focus] ?? null);
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [rows, focus, searchId]);

  return { focus, setFocus, open, setOpen };
}

/** The standing hint strip under a queue. One string, so every screen says it identically. */
export const QUEUE_HINT = 'j / k move · Enter opens · Esc closes · / search';
